import { create } from 'zustand';
import { ANSICHTEN_STANDARD, type Ansichten } from '@etown/shared';
import type { ViewMode, ViewSettings } from '../game/view';
import { normalisiereStil3d } from '../renderer3d/theme/stil3dAuswahl';
import { schreibeTheme3dWahl, theme3dWahl } from '../renderer3d/theme/theme3dWahl';

const MODE_KEY = 'etown.viewMode';
const SETTINGS_KEY = 'etown.viewSettings';

/** Voreinstellung der Ego-Ansicht — bewusst zurueckhaltend. */
export const DEFAULT_VIEW_SETTINGS: ViewSettings = {
  sensitivity: 0.0022,
  fieldOfView: 70,
  headBob: true,
  ceiling: true,
  quality: 1,
};

export const SENSITIVITY_RANGE = { min: 0.0008, max: 0.005 };
export const FIELD_OF_VIEW_RANGE = { min: 55, max: 95 };
export const QUALITY_RANGE = { min: 0.6, max: 1 };

/**
 * Ansicht und ihre Einstellungen sind reine Geraete-Einstellungen: sie liegen
 * im localStorage, nicht auf dem Server. Der Server muss nicht wissen, wer in
 * welcher Ansicht unterwegs ist — beide sehen dieselbe Welt.
 */
export function loadViewModeVorliebe(): ViewMode {
  return loadViewMode();
}

function loadViewMode(): ViewMode {
  try {
    return window.localStorage.getItem(MODE_KEY) === '3d' ? '3d' : '2d';
  } catch {
    return '2d';
  }
}

/**
 * Gespeicherte Einstellungen in gueltige Werte ueberfuehren. Der Inhalt des
 * localStorage stammt aus einer frueheren Version oder wurde von Hand
 * bearbeitet — fehlende und unsinnige Werte fallen auf die Voreinstellung
 * zurueck, statt die Ansicht unbenutzbar zu machen.
 */
export function normalizeViewSettings(stored: unknown): ViewSettings {
  const raw = (typeof stored === 'object' && stored !== null ? stored : {}) as Partial<ViewSettings>;
  return {
    sensitivity: clamp(raw.sensitivity, SENSITIVITY_RANGE, DEFAULT_VIEW_SETTINGS.sensitivity),
    fieldOfView: clamp(raw.fieldOfView, FIELD_OF_VIEW_RANGE, DEFAULT_VIEW_SETTINGS.fieldOfView),
    headBob: typeof raw.headBob === 'boolean' ? raw.headBob : DEFAULT_VIEW_SETTINGS.headBob,
    ceiling: typeof raw.ceiling === 'boolean' ? raw.ceiling : DEFAULT_VIEW_SETTINGS.ceiling,
    quality: clamp(raw.quality, QUALITY_RANGE, DEFAULT_VIEW_SETTINGS.quality),
  };
}

function loadSettings(): ViewSettings {
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    return normalizeViewSettings(raw ? JSON.parse(raw) : null);
  } catch {
    // Kaputter oder gesperrter Speicher: mit den Voreinstellungen weitermachen
    return DEFAULT_VIEW_SETTINGS;
  }
}

function clamp(value: number | undefined, range: { min: number; max: number }, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(value, range.min), range.max);
}

interface ViewState {
  mode: ViewMode;
  /** Was die aktuelle Karte anbietet; entscheidet, ob der Umschalter erscheint. */
  ansichten: Ansichten;
  /** true, waehrend der Renderer getauscht wird. */
  switching: boolean;
  settings: ViewSettings;
  /**
   * Ausdruecklich gewaehlter Stil der Ego-Ansicht; `null` heisst "keine
   * Wahl", dann entscheidet die Registry. Er liegt bewusst neben den
   * `settings` und nicht darin: die sind das Paket, das bei jedem
   * Ansichtswechsel an Renderer und Steuerung durchgereicht wird, und der
   * Stil ist keine Einstellung des Renderers — er waehlt aus, welcher
   * gebaut wird. Ausserdem behaelt er so seinen alten Speicherplatz.
   */
  stil3d: string | null;
  setMode: (mode: ViewMode) => void;
  /**
   * Modus nur fuer diese Karte setzen, ohne die Geraetevorliebe zu
   * ueberschreiben — ein Stadion erzwingt 3D, das Buero soll danach wieder
   * so starten wie zuvor.
   */
  setModeFluechtig: (mode: ViewMode) => void;
  setAnsichten: (ansichten: Ansichten) => void;
  setSwitching: (switching: boolean) => void;
  updateSettings: (patch: Partial<ViewSettings>) => void;
  setStil3d: (stil3d: string | null) => void;
}

export const useViewStore = create<ViewState>((set, get) => ({
  mode: loadViewMode(),
  ansichten: ANSICHTEN_STANDARD,
  switching: false,
  settings: loadSettings(),
  // Beim Start gewinnt `?theme3d=…` aus der Adresse; `theme3dWahl` schreibt
  // sie dabei selbst fort. Unsinn im Speicher wird zu null und faellt damit
  // auf den Standard zurueck.
  stil3d: normalisiereStil3d(theme3dWahl()),
  setMode: (mode) => {
    persist(MODE_KEY, mode);
    set({ mode });
  },
  setModeFluechtig: (mode) => set({ mode }),
  setAnsichten: (ansichten) => set({ ansichten }),
  setSwitching: (switching) => set({ switching }),
  updateSettings: (patch) => {
    const settings = { ...get().settings, ...patch };
    persist(SETTINGS_KEY, JSON.stringify(settings));
    set({ settings });
  },
  setStil3d: (stil3d) => {
    schreibeTheme3dWahl(stil3d);
    set({ stil3d });
  },
}));

function persist(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Privater Modus ohne Speicher: gilt dann nur fuer diese Sitzung
  }
}
